import { useState, FormEvent } from 'react';

interface Props {
  onClose: () => void;
}

export default function ConsultationModal({ onClose }: Props) {
  const [formData, setFormData] = useState({
    parentName: '',
    studentName: '',
    grade: '中学3年生',
    phone: '',
    email: '',
    currentScore: '',
    targetSchool: '',
    consultationType: '志望校相談',
    preferredDate: '',
    preferredTime: '',
    concerns: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6a6q2lbiueuoqm1s9cg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto">
        {/* ヘッダー */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <h3 className="text-xl font-bold text-[#1A2B4A]">受験相談予約</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-2xl text-[#1A2B4A]"></i>
          </button>
        </div>

        {/* フォーム */}
        <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="p-6">
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
              <i className="ri-check-line mr-2"></i>
              送信が完了しました。担当者より折り返しご連絡いたします。
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">
              <i className="ri-error-warning-line mr-2"></i>
              送信に失敗しました。もう一度お試しください。
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                保護者様のお名前 <span className="text-[#FF6B35]">*</span>
              </label>
              <input
                type="text"
                name="parentName"
                required
                value={formData.parentName}
                onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                生徒様のお名前 <span className="text-[#FF6B35]">*</span>
              </label>
              <input
                type="text"
                name="studentName"
                required
                value={formData.studentName}
                onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="山田 花子"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                学年 <span className="text-[#FF6B35]">*</span>
              </label>
              <select
                name="grade"
                required
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
              >
                <option value="中学3年生">中学3年生</option>
                <option value="中学2年生">中学2年生</option>
                <option value="中学1年生">中学1年生</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                電話番号 <span className="text-[#FF6B35]">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="090-1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                メールアドレス <span className="text-[#FF6B35]">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                相談内容 <span className="text-[#FF6B35]">*</span>
              </label>
              <select
                name="consultationType"
                required
                value={formData.consultationType}
                onChange={(e) => setFormData({ ...formData, consultationType: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
              >
                <option value="志望校相談">志望校相談</option>
                <option value="学習方法相談">学習方法相談</option>
                <option value="内申点対策">内申点対策</option>
                <option value="偏差値アップ">偏差値アップ</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                現在の成績（分かる範囲で）
              </label>
              <input
                type="text"
                name="currentScore"
                value={formData.currentScore}
                onChange={(e) => setFormData({ ...formData, currentScore: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="例：偏差値50、内申点32"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                志望校（分かる範囲で）
              </label>
              <input
                type="text"
                name="targetSchool"
                value={formData.targetSchool}
                onChange={(e) => setFormData({ ...formData, targetSchool: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
                placeholder="例：廿日市高校"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                希望日
              </label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                希望時間帯
              </label>
              <select
                name="preferredTime"
                value={formData.preferredTime}
                onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
              >
                <option value="">選択してください</option>
                <option value="10:00-12:00">10:00-12:00</option>
                <option value="13:00-15:00">13:00-15:00</option>
                <option value="15:00-17:00">15:00-17:00</option>
                <option value="17:00-19:00">17:00-19:00</option>
                <option value="19:00-21:00">19:00-21:00</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1A2B4A] mb-2">
                お悩み・ご相談内容
              </label>
              <textarea
                name="concerns"
                value={formData.concerns}
                onChange={(e) => setFormData({ ...formData, concerns: e.target.value })}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm resize-none"
                placeholder="現在のお悩みや相談したい内容をお書きください（500文字以内）"
              ></textarea>
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.concerns.length}/500
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-6 bg-[#FF6B35] text-white font-bold py-4 rounded-full hover:bg-[#FF6B35]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? '送信中...' : '受験相談を予約する'}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            送信いただいた情報は、お問い合わせ対応のみに使用いたします
          </p>
        </form>
      </div>
    </div>
  );
}
